import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, Send, Loader2 } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SITE } from "@/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOALS = ["Weight loss", "Build strength", "Rehab / recovery", "Mobility & health", "General fitness", "Not sure yet"];
const EMPTY = { name: "", email: "", phone: "", location: "", goal: "", message: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      toast.success("Thanks! Your enquiry is in — I'll be in touch very soon.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call or WhatsApp me instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#020617]">
      <div className="mx-auto max-w-7xl px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-5">
          <span className="font-mono-label text-[11px] text-[#FACC15]">Get started</span>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl mt-4">
            Let's find <span className="text-[#FACC15]">your start.</span>
          </h2>
          <p className="text-[#94A3B8] mt-5 text-base md:text-lg leading-relaxed">
            Tell me a little about you and your goals. No pressure, no jargon — just a friendly
            chat about how we can get you moving.
          </p>

          <div className="mt-10 space-y-3">
            <a href={`tel:${SITE.phoneRaw}`} data-testid="contact-phone"
               className="flex items-center gap-4 p-4 rounded-2xl border border-white/10 hover:border-[#FACC15]/60 transition-colors duration-300">
              <span className="w-11 h-11 rounded-full bg-[#FACC15] text-[#020617] flex items-center justify-center"><Phone size={18} /></span>
              <div><p className="font-mono-label text-[10px] text-[#94A3B8]">Call</p><p className="font-display text-lg">{SITE.phoneDisplay}</p></div>
            </a>
            <a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
               className="flex items-center gap-4 p-4 rounded-2xl border border-white/10 hover:border-[#FACC15]/60 transition-colors duration-300">
              <span className="w-11 h-11 rounded-full bg-[#FACC15] text-[#020617] flex items-center justify-center"><MessageCircle size={18} /></span>
              <div><p className="font-mono-label text-[10px] text-[#94A3B8]">WhatsApp</p><p className="font-display text-lg">Message me</p></div>
            </a>
            <a href={`mailto:${SITE.email}`} data-testid="contact-email"
               className="flex items-center gap-4 p-4 rounded-2xl border border-white/10 hover:border-[#FACC15]/60 transition-colors duration-300">
              <span className="w-11 h-11 rounded-full bg-[#FACC15] text-[#020617] flex items-center justify-center"><Mail size={18} /></span>
              <div><p className="font-mono-label text-[10px] text-[#94A3B8]">Email</p><p className="font-display text-lg">{SITE.email}</p></div>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form onSubmit={submit} data-testid="lead-form" className="p-6 md:p-10 rounded-3xl border border-white/10 bg-[#0F172A]">
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label="Your name *" testid="lead-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              <Field label="Phone *" testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="07XXX XXXXXX" />
              <Field label="Email *" testid="lead-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" />
              <Field label="Location (town)" testid="lead-location" value={form.location} onChange={set("location")} placeholder="Chelmsford" />
            </div>

            <div className="mt-6">
              <label className="font-mono-label text-[10px] text-[#94A3B8]">Main goal</label>
              <select value={form.goal} onChange={set("goal")} data-testid="lead-goal"
                      className="line-input w-full py-3 mt-2 text-base [&>option]:bg-[#0F172A]">
                <option value="">Select a goal…</option>
                {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>

            <div className="mt-6">
              <label className="font-mono-label text-[10px] text-[#94A3B8]">Tell me a bit more *</label>
              <textarea value={form.message} onChange={set("message")} data-testid="lead-message" rows={4}
                        placeholder="Your goals, any conditions, what space you have…"
                        className="line-input w-full py-3 mt-2 text-base resize-none" />
            </div>

            <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={loading} data-testid="lead-submit"
              className="mt-8 w-full flex items-center justify-center gap-2 bg-[#FACC15] text-[#020617] font-mono-label text-xs font-semibold py-5 rounded-full hover:bg-[#EAB308] transition-colors duration-300 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? "Sending…" : "Send my enquiry"}
            </motion.button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const Field = ({ label, testid, type = "text", value, onChange, placeholder }) => (
  <div>
    <label className="font-mono-label text-[10px] text-[#94A3B8]">{label}</label>
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} data-testid={testid}
           className="line-input w-full py-3 mt-2 text-base" />
  </div>
);
